import moment from "moment";
import { getService } from "react-in-angularjs";
import {
    contributeAiCapabilityParamOption,
    declareAiCapability,
    provideAiCapability
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

/**
 * Filling in a report's own filters.
 *
 * The field is an enum with no options here, because a report's filters are the report's - one to six
 * of them, never the same set twice. The open report contributes them below and takes them away when
 * it goes, so the model is offered exactly the filters in front of the person and nothing else.
 *
 * `scope: "standard-report"` is opened by standardReport.open, which is how "the nursing visits due
 * report for last month" becomes open-then-fill without either side naming the other.
 */
declareAiCapability({
    id: "standardReport.filter",
    title: "Fill in a filter on the open report",
    effect: "prefill",
    description:
        "Types a value into one of the filters of the report that is open, the same as filling it in by hand. One filter per step, named in the words the page uses - a date range is two filters, a from and a to, so that is two steps. Both params are required, so every value needs a step of its own: never leave a filter value in searchTerms, and put several values for one filter in that filter's own step. It does not run the report - the person presses Run.",
    location: "the Filters panel of a standard report",
    scope: "standard-report",
    permissionKey: "view_reports_section_standard",
    params: [
        {
            name: "field",
            type: "enum",
            required: true,
            description:
                "Which one of the report's filters to fill in. There is no combined date-range filter: a period means one step for the earliest date and another for the latest."
        },
        {
            name: "value",
            type: "text",
            // A multiselect filter takes several: "france and germany" is one Contracts filter with
            // two values, not two filters or a word left over.
            multiple: true,
            required: true,
            description:
                "What to put in it, or several for a filter that takes a list of them. A date must be an actual yyyy-MM-dd - work out what \"the past year\" or \"last month\" means yourself, from today's date, and never pass the words. Otherwise true/false, a number, or the name of an option like a contract, office or status, in the user's words."
        }
    ],
    examples: [
        "visit dates in the past year",
        "only the France contract",
        "active patients only"
    ]
});

/** Every filter list the page can offer, by the srcType the template names. */
const MULTISELECT_SOURCES = {
    OFFICE: (managers) => managers.office.getOffices(),
    CONTRACT_TYPE: (managers) => managers.contractTypes.getActiveContractTypes(),
    SERVICE_CODE: (managers) => managers.serviceCodes.getActiveServiceCodes(),
    PAY_CODE: (managers) => managers.payCodes.getActivePayCodes(),
    PATIENT_STATUS: (managers) => managers.patientStatuses.getAllPatientStatuses(),
    CAREGIVER_STATUS: (managers) => managers.caregiverStatuses.getAllCaregiverStatuses(),
    CAREGIVER_CERTIFICATION: (managers) => managers.certifications.getCertifications()
};

const normaliseWords = (text) =>
    ` ${String(text).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()} `;

/**
 * The option a word names, from the list this filter draws on.
 *
 * Whole name first, then a run of whole words of one and only one option - the same ladder the omnibar
 * uses for its own enums, and for the same reason: a near miss is worth refusing out loud.
 */
function findFilterOption(options, requested) {
    const wanted = normaliseWords(requested);
    const labelled = options.map((option) => ({
        option,
        words: normaliseWords(option.name ?? option.id)
    }));

    const exact = labelled.find((entry) => entry.words === wanted);

    if (exact !== undefined) {
        return { type: "one", option: exact.option };
    }

    const containing = labelled.filter(
        (entry) => entry.words.includes(wanted) || wanted.includes(entry.words)
    );

    if (containing.length === 1) {
        return { type: "one", option: containing[0].option };
    }

    return {
        type: containing.length === 0 ? "none" : "many",
        options: containing.map((entry) => entry.option)
    };
}


//! @ngInject
export function standardReportViewCtrl(
    $scope,
    $rootScope,
    $state,
    $filter,
    $uibModal,
    reportService,
    uiGridExporterConstants,
    DatabaseApi,
    toaster
  ) {
    $scope.goToReportState = (reportState) => {
        $state.go(reportState);
    }


$scope.handleBack = () =>{
  $state.go('app.reports.standard', {}, { reload: true });
}
    const parseFilters = (filters) => {
      for (let i = 0; i < filters.length; i++) {
        let item = filters[i];

      
        if (item["aggregate"] === undefined) {
          filters[i].aggregate = null;
        }
        if (!filters[i].filterSelected) {
          filters.splice(i);
        }
        if (item["$selectedModel"]) {
          item.value = [];
          for (let j = 0; j < item["$selectedModel"].length; j++) {
            let selectedArrayItem = item["$selectedModel"][j];
            item.value.push(selectedArrayItem.id);
          }
          if (item.value.length === 0) {
          item.filterSelected  = false;
          }
        }
      }

      return filters.filter((val)=>{
        return val.filterSelected == true ? true:  false;
      }).map((filter) => ({
        ...filter,
        tableColumn: filter.name,
        // aggregate: null,
        isLocked: filter.isLocked,
        value: parseFilterValueToObject(filter),
      }));
    };

    const parseFilterValueToObject = (filter) => {
      let valueString = parseFilterValueString(filter);
      
      let valueObj = {};
      if (filter.type === "MULTISELECT") {
        return {
          type: "multiselect",
          value: filter.value,
        };
      }
      switch (filter.operator) {
        case "LAST X":
        case "THIS X":
        case "NEXT X": {
          valueObj = {
            type: "timePeriod",
            timePeriod: valueString,
          };
          break;
        }
        case "LAST N X":
        case "NEXT N X": {
          valueObj = {
            type: "timePeriodInterval",
            value: {
              interval: filter.timePeriodValue.interval,
              timePeriod: filter.timePeriodValue.timePeriod,
            },
          };
          break;
        }
        case ("LAST REL N X"):
        case ("NEXT REL N X"):  {
          valueObj = {
            type: "timePeriodNowRelative",
            value: {
              negate: false,
              interval: filter.timePeriodValue.interval,
              timePeriod: filter.timePeriodValue.timePeriod,
            },
          };
          break;
        }
        case ("NOW REL TIME COMPARE"):
          valueObj = {
            type: "timeComparisonNowRelative",
            value: {
              comparison: filter.timePeriodValue.comparison,
              interval: filter.timePeriodValue.interval,
              timePeriod: filter.timePeriodValue.timePeriod
            }
          }
        break;
        default:
          valueObj = {
            type: "string",
            value: valueString,
          };
          break;
      }

        return valueObj;
    }

    const parseFilterValueString = (filter) => {
        switch (filter.type) {
            case "TIMESTAMPTZ":
            case "TIMESTAMP":
            case "DATE":
                return $filter("mfShortTime")(filter.value, ['withDate', 'withSeconds'])
            case "TIME":
                return $filter("mfShortTime")(filter.value, ['withSeconds'])
            case "MULTISELECT":
                return filter.value;
            default:
                return "" + filter.value;
        }
    }
    $scope.export = (format) => {
      if (format == 'csv') {
        var myElement = angular.element(document.querySelectorAll(".custom-csv-link-location"));
        var rowTypes = uiGridExporterConstants.ALL;
        var colTypes = uiGridExporterConstants.ALL;
        // this.gridOptions.exporterCsvFilename = getFileName(this.reportName);
        $rootScope.gridApi.exporter.csvExport(rowTypes,colTypes,myElement );
      } else if (format == 'xlsx') {

        var rowTypes = uiGridExporterConstants.ALL;
        var colTypes = uiGridExporterConstants.ALL;
        $rootScope.gridApi.exporter.excelExport(rowTypes,colTypes );
      };
    };

    const parseSavedFilterValueToValue = (filter) => {
        if (filter.type === "NUMBER") {
            return (Number.parseInt(filter.value.value, 10));
        }

        if (filter.type === "BOOLEAN") {
            return (filter.value.value === "true");
        }

        let newValue;

        switch (filter.operator) {
            case ("LAST X"):
            case ("THIS X"):
            case ("NEXT X"): {
                newValue = filter.value.timePeriod;
                break;
            }
            case ("LAST N X"):
            case ("NEXT N X"): 
            case ("LAST REL N X"):
            case ("NEXT REL N X"): {
                newValue = {
                    interval: filter.value.interval,
                    timePeriod: filter.value.timePeriod
                }
                break;
            }
            case ("NOW REL TIME COMPARE"):
              newValue = {
                comparison: filter.value.comparison,
                interval: filter.value.interval,
                timePeriod: filter.value.timePeriod
              }
              break;
            default:
                newValue = filter.value.value;
                break;
        }

        return newValue;
    }
    $scope.loadingReport = false;
    $scope.gridOptions = {};

    // Reports without a table definition get their columns from the data, and one report can return a
    // different set of columns per filter (e.g. the "Report" filter of a statistical report). ui-grid
    // only derives columns from the first data it sees, so they are re-derived here whenever they change.
    let hasPredefinedColumnDefs = false;

    const setReportLines = (lines) => {
        if (!hasPredefinedColumnDefs) {
            const columnNames = lines.length > 0
                ? Object.keys(lines[0]).filter((name) => name !== "$$hashKey")
                : [];
            const currentColumnNames = ($scope.gridOptions.columnDefs ?? []).map((column) => column.name);

            if (!angular.equals(columnNames, currentColumnNames)) {
                $scope.gridOptions.columnDefs = columnNames.map((name) => ({ name }));
            }
        }

        $scope.gridOptions.data = lines;
    };
    const isValueEmpty = (filterValue, filterOperator) => {
        return ((filterValue === undefined || filterValue === "") && !["IS NULL", "NOT IS NULL"].includes(filterOperator));
    }
    const handleLoadStandardReport = (reportId) => {

        if (!reportId) {
            return;
        }
        reportService.getStandardReport(reportId).then((loadedSavedReport) => {

            $scope.gridOptions = {
                ...loadedSavedReport.report_table_def,
                exporterMenuCsv: true,
                exporterMenuPdf: false,
                enableGridMenu: true,
                enableGroupHeaderSelection: true,
                treeRowHeaderAlwaysVisible: false,
                paginationPageSizes: [25, 50, 100],
                paginationPageSize: 50,
                showColumnFooter:false,
                onRegisterApi: function(gridApi){
                    $rootScope.gridApi = gridApi;
                },
                enableFiltering: true,
                useExternalFiltering: false,
                useExternalPagination: false

            };
            hasPredefinedColumnDefs = ($scope.gridOptions.columnDefs ?? []).length > 0;
            $scope.report = {
                ...loadedSavedReport.report_template,
                reportType:loadedSavedReport.report_type,
                id: loadedSavedReport.id,
                name: loadedSavedReport.name,
                description: loadedSavedReport.description,
                primaryTable: loadedSavedReport.report_type === 'CUSTOM' ? null : loadedSavedReport.report_template.primaryTable.tableName,
            };
            for (const filter of loadedSavedReport.report_template.filters) {
                filter.value = parseSavedFilterValueToValue(filter);
            }

            // Only now: the filters are what the capability's vocabulary is made of, and until this
            // point there is nothing to offer.
            publishFiltersToAi($scope.report.filters ?? []);
        }).catch(err => toaster.pop("error", "Something went wrong", "Failed to load saved report"));
    }

    // Data managers for the lists a MULTISELECT filter draws on - the same ones the filter's own
    // dropdown is built from. Made once, on first use.
    let filterManagers = null;

    const getFilterManagers = () => {
        if (filterManagers === null) {
            const pageManager = getService("itemSearchPageManager").createSearchPageManager("");

            filterManagers = {
                office: pageManager.getOfficeDataManager(),
                contractTypes: pageManager.getContractTypesDataManager(),
                serviceCodes: pageManager.getServiceCodesDataManager(),
                payCodes: pageManager.getPayCodesDataManager(),
                patientStatuses: pageManager.getPatientStatusesDataManager(),
                caregiverStatuses: pageManager.getCaregiverStatusesDataManager(),
                certifications: pageManager.getCertificationDataManager()
            };
        }

        return filterManagers;
    };

    const setFilterValue = (filter, requested) => {
        // Only a multiselect can hold more than one. Anywhere else, a list is a request the filter
        // cannot honour, and saying so beats quietly using the first.
        if (Array.isArray(requested) && filter.type !== "MULTISELECT") {
            throw new Error(
                filter.label + " takes one value, not " + requested.length + "."
            );
        }

        switch (filter.type) {
            case "TIMESTAMPTZ":
            case "TIMESTAMP":
            case "DATE": {
                if (!/^\d{4}-\d{2}-\d{2}$/.test(requested)) {
                    throw new Error('"' + requested + '" is not a date I can read.');
                }

                const [year, month, day] = requested.split("-").map(Number);

                filter.value = new Date(year, month - 1, day);

                return $filter("date")(filter.value, $rootScope.dateFormat);
            }

            case "TIME":
            case "STRING":
                filter.value = requested;

                return requested;

            case "NUMBER": {
                const asNumber = Number(requested);

                if (!Number.isFinite(asNumber)) {
                    throw new Error('"' + requested + '" is not a number.');
                }

                filter.value = asNumber;

                return String(asNumber);
            }

            case "BOOLEAN":
                filter.value = String(requested).toLowerCase() === "true";

                return filter.value ? "yes" : "no";

            case "MULTISELECT": {
                const source = MULTISELECT_SOURCES[filter.srcType];

                if (source === undefined) {
                    throw new Error(
                        "I cannot fill in " + filter.label + " yet - it draws on the " +
                        filter.srcType + " list."
                    );
                }

                const options = source(getFilterManagers()) ?? [];

                // Told apart from a genuine miss: an empty list means the agency data behind this
                // filter has not arrived, which is a different thing from the value not existing.
                if (options.length === 0) {
                    throw new Error(
                        "The list of " + filter.label + " has not loaded yet - try again in a moment."
                    );
                }

                /** Where a word can be found on this report: this filter first, then the others. */
                const findHome = (one) => {
                    const here = findFilterOption(options, one);

                    if (here.type === "one") {
                        return { filter, option: here.option };
                    }

                    if (here.type === "many") {
                        throw new Error(
                            '"' + one + '" matches more than one ' + filter.label + ": " +
                            here.options.map((option) => option.name ?? option.id).join(", ") +
                            ". Which one?"
                        );
                    }

                    // Not in this filter's list. The plan often names one filter and then gives
                    // values belonging to two - "france and europe" is a contract and an office - so
                    // the word goes wherever it really lives rather than failing here.
                    const elsewhere = ($scope.report.filters ?? [])
                        .filter((other) => other !== filter && other.type === "MULTISELECT")
                        .flatMap((other) => {
                            const otherSource = MULTISELECT_SOURCES[other.srcType];

                            if (otherSource === undefined) {
                                return [];
                            }

                            const found = findFilterOption(
                                otherSource(getFilterManagers()) ?? [],
                                one
                            );

                            return found.type === "one" ? [{ filter: other, option: found.option }] : [];
                        });

                    if (elsewhere.length === 1) {
                        return elsewhere[0];
                    }

                    throw new Error(
                        elsewhere.length > 1
                            ? '"' + one + '" could be ' +
                              elsewhere.map((hit) => hit.filter.label).join(" or ") + ". Which?"
                            : 'There is no "' + one + '" in ' + filter.label +
                              " or any other filter on this report."
                    );
                };

                // Several values arrive either as a list (each already matched to a list) or as one
                // phrase of free text. Try a phrase whole first - a real option may contain "and".
                // Whole-phrase first, but only on an exact name: the looser contains match would
                // let "france, europe" pass as the France option and quietly lose the rest.
                const isWholeName = options.some(
                    (option) =>
                        normaliseWords(option.name ?? option.id) === normaliseWords(requested)
                );

                const asked = Array.isArray(requested)
                    ? requested
                    : isWholeName
                      ? [requested]
                      : String(requested)
                            .split(/\s*(?:,|;|\+|&|\band\b)\s*/i)
                            .map((part) => part.trim())
                            .filter((part) => part !== "");

                const homes = new Map();

                for (const one of asked) {
                    const home = findHome(one);
                    const already = homes.get(home.filter) ?? [];

                    if (!already.some((option) => option.id === home.option.id)) {
                        homes.set(home.filter, [...already, home.option]);
                    }
                }

                const described = [];

                for (const [target, chosen] of homes) {
                    // The dropdown reads $selectedModel; the required hidden input reads value[0].
                    target.$selectedModel = chosen;
                    target.value = chosen;
                    described.push(
                        target.label + " to " +
                        chosen.map((option) => option.name ?? option.id).join(" and ")
                    );
                }

                return { message: "Set " + described.join(", ") };
            }

            default:
                throw new Error("I cannot fill in a " + filter.type + " filter yet.");
        }
    };

    let releaseFilterOptions = [];
    let releaseFilterCapability = null;

    const publishFiltersToAi = (filters) => {
        releaseFilterOptions.forEach((release) => release());
        releaseFilterOptions = filters
            .filter((filter) => filter.name && filter.label)
            .map((filter) =>
                contributeAiCapabilityParamOption("standardReport.filter", "field", {
                    value: filter.name,
                    label: filter.label
                })
            );

        // The lists a MULTISELECT filter chooses from are DatabaseApi caches, filled by a bulk fetch
        // after login. Reading one before it lands gives an empty list, and "france" then looks like a
        // contract this agency does not have. So availability waits for the lists *this* report needs
        // - the managers re-read DatabaseApi on every call, so polling is enough to notice.
        const neededSources = filters
            .filter((filter) => filter.type === "MULTISELECT" && filter.srcType)
            .map((filter) => filter.srcType);

        const listsReady = () =>
            neededSources.every((srcType) => {
                const source = MULTISELECT_SOURCES[srcType];

                // A source we cannot read is not something to wait for; the runner says so by name.
                return source === undefined || (source(getFilterManagers()) ?? []).length > 0;
            });

        const provideWhenListsReady = (attempt) => {
            if (releaseFilterCapability !== null) {
                return;
            }

            if (!listsReady() && attempt < 40) {
                getService("$timeout")(() => provideWhenListsReady(attempt + 1), 250);

                return;
            }

            provideFilterCapability();
        };

        const provideFilterCapability = () => {
            releaseFilterCapability = provideAiCapability("standardReport.filter", (args) => {
                const filter = ($scope.report.filters ?? []).find(
                    (candidate) => candidate.name === String(args.field)
                );

                if (filter === undefined) {
                    throw new Error("This report has no " + String(args.field) + " filter.");
                }

                // Inside a digest: the filter inputs are bound straight to these fields.
                return getService("$timeout")(() => {
                    const shown = setFilterValue(
                        filter,
                        Array.isArray(args.value) ? args.value.map(String) : String(args.value)
                    );

                    // A multiselect may have placed values on more than one filter, so it writes its
                    // own line; everything else just reports what it now holds.
                    return typeof shown === "object" && shown !== null
                        ? shown.message
                        : "Set " + filter.label + " to " + shown;
                });
            });
        };

        provideWhenListsReady(0);
    };

    $scope.$on("$destroy", function () {
        releaseFilterOptions.forEach((release) => release());
        releaseFilterOptions = [];

        if (releaseFilterCapability !== null) {
            releaseFilterCapability();
            releaseFilterCapability = null;
        }
    });

    $scope.handleRemoveFilter = (index, tableName, columnName) => {
        $scope.reportsSchema.tables.find(table => table.tableName === tableName).columns.find(column => column.name === columnName).filterSelected = false;
        $scope.report.filters.splice(index, 1);
    }

    $scope.handleRunReport = () => {
      $scope.loadingReport = true;
      const currentReport = angular.copy($scope.report);
      
      const reportToSave = {
        ...currentReport,
        format: "json",
        primaryTable:
          currentReport.report_type === "REPORTING_TOOL"
            ? { tableName: currentReport.primaryTable, columns: [] }
            : null,

        // tables: getTablesArrayByColumns(currentReport.columns),
        // columns: parseColumns(currentReport.columns),
        filters: parseFilters(currentReport.filters),
        timezone: moment.tz.guess(true),
      };
      if (reportToSave.reportType === "CUSTOM") {
        
        reportService
          .executeCustomReport(reportToSave)
          .then((data) => {
            setReportLines(data.lines);

                toaster.pop("success", "Success", "Successfully created report");
            }).catch((error) => {
                // console.log(error);
                let err = error.data;
                toaster.pop("error", err.error, err.details.join("\n"));                
                // toaster.pop("error", err.error, err.details);
            }).finally(() => {
                $scope.loadingReport = false;
            });

        }else {
            reportService.executeReport(reportToSave).then((data) => {
                toaster.pop("success", "Success", "Successfully created report");

                setReportLines(data.lines);

            }).catch((error) => {
                
                let err = error.data;
                
                toaster.pop("error", err.error, err.details.join("\n"));
                // toaster.pop("error", "Something went wrong", "Failed to create report");
            }).finally(() => {
                $scope.loadingReport = false;
            });
        }
    }
    function initialize() {
        $scope.savedReportId = $state.params.standardReportId;
        handleLoadStandardReport($scope.savedReportId);
        $scope.isAdmin = $rootScope.user.admin;
    }

    initialize();
};
